import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Badge, SectionTitle } from "@/components/ui-hud";
import ProfileChecklist from "@/components/ProfileChecklist";
import FleetLiveMap from "@/components/FleetLiveMap";
import AirportInput from "@/components/AirportInput";
import ScrollHints from "@/components/ScrollHints";
import { Plane, Wrench, Store, MessageSquare, Sparkles, ShieldCheck, Users, TrendingUp, Radar, AlertTriangle, ArrowRight, Send, Fuel, Building2, PlusCircle, Edit3, ChevronDown, ChevronUp, Megaphone, X, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard({ embedded = false }) {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [contracts, setContracts] = useState([]);
  const [aog, setAog] = useState([]);
  const [topics, setTopics] = useState([]);
  const [posted, setPosted] = useState([]);
  const [fleet, setFleet] = useState([]);

  const isOperator = user?.role === "charter_operator";

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get("/stats").then((r) => setStats(r.data)),
      api.get("/contracts").then((r) => setContracts(r.data.slice(0, 4))),
      api.get("/aog").then((r) => setAog(r.data.slice(0, 3))),
      api.get("/forum/topics").then((r) => setTopics(r.data.slice(0, 4))),
      api.get("/contracts/mine").then((r) => setPosted(r.data)).catch(() => {}),
      isOperator
        ? api.get("/operators/me/fleet").then((r) => setFleet(r.data.fleet || [])).catch(() => {})
        : Promise.resolve(),
    ]).catch(() => {});
  }, [user, isOperator]);

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24 text-center">
        <div className="overline text-orange-hud mb-4">AUTH REQUIRED</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mb-6">Access the cockpit.</h1>
        <div className="flex justify-center gap-3">
          <Link to="/login" data-testid="dash-login" className="btn-hud">SIGN IN</Link>
          <Link to="/register" data-testid="dash-register" className="btn-hud btn-hud-solid">JOIN PJP</Link>
        </div>
      </div>
    );
  }

  const applicationsTotal = posted.reduce((n, p) => n + (p.application_count || 0), 0);

  // When embedded inside Cockpit, drop the outer max-w wrapper (the Cockpit
  // shell handles centering) and trim the top padding so the section reads
  // as part of the unified scroll.
  const outerCls = embedded
    ? "pt-2 pb-8"
    : "max-w-7xl mx-auto px-6 py-12";

  return (
    <div className={outerCls}>
      <ScrollHints testIdPrefix="cockpit-scroll" />
      {/* Header strip — hidden when embedded in Cockpit since the shell already
          shows the user's identity + status banner up top. Removes the
          redundant "Welcome back" that used to sit halfway down the page. */}
      {!embedded && (
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="overline text-cyan mb-2 flex items-center gap-2">
              <span className="h-1.5 w-1.5 bg-avail rounded-full animate-pulse" />
              COCKPIT · {new Date().toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }).toUpperCase()}
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Welcome back, {user.first_name}.</h1>
          </div>
          <div className="flex gap-3">
            <Link to="/aerointel" data-testid="dash-cta-aerointel" className="btn-hud"><Sparkles className="h-3.5 w-3.5" /> ASK AEROINTEL</Link>
            <Link to="/inbox" data-testid="dash-cta-inbox" className="btn-hud btn-hud-solid">
              INBOX {applicationsTotal > 0 && <span className="ml-1">· {applicationsTotal}</span>}
            </Link>
          </div>
        </div>
      )}

      {/* Instrument row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 stagger">
        <Instrument icon={Users} to="/directory" label="MEMBERS" value={(stats.members || 26511).toLocaleString()} tone="cyan" />
        <Instrument icon={Plane} to="/jobs" label="OPEN CONTRACTS" value={stats.contracts_open ?? 0} tone="cyan" />
        <Instrument icon={AlertTriangle} to="/aog" label="ACTIVE AOG" value={stats.aog_active ?? 0} tone="orange" />
        <Instrument icon={Store} to="/marketplace" label="MARKETPLACE" value={stats.marketplace_listings ?? 0} tone="cyan" />
      </div>

      {/* Onboarding checklist — compact view.
          Hidden in embedded (Cockpit) mode because the FAA card in the
          Profile section now shows a mini checklist right next to PHF. */}
      {!embedded && (
        <div className="mb-8">
          <ProfileChecklist compact title="Your PJP Checklist" />
        </div>
      )}

      {/* Empty Leg Program promo card — role-aware. Skipped for operators
          because the OperatorHub renders a first-class version below. */}
      {!isOperator && <EmptyLegPromo isOperator={isOperator} />}

      {/* Panels */}
      <div className="grid lg:grid-cols-3 gap-5">
        {/* Verified profile card — glassmorphic Pilot Card, matches what
            visitors see in Directory search. Shows avatar, home base,
            type ratings and current availability. */}
        <PilotCard user={user} embedded={embedded} />

        {/* Latest jobs */}
        <Card testId="dash-jobs" className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline text-cyan">FLYING JOBS</div>
              <div className="font-display text-xl font-semibold mt-1">Fresh contract opportunities</div>
            </div>
            <Link to="/jobs" data-testid="dash-all-jobs" className="btn-hud">ALL JOBS <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
          <div className="space-y-3">
            {contracts.map((c) => (
              <Link key={c.id} to="/jobs" className="block border border-cyan/10 hover:border-cyan/40 transition-colors p-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge tone="cyan">{c.role_needed.replace("_", " ").toUpperCase()}</Badge>
                      <div className="overline">{c.posted_by_name}</div>
                    </div>
                    <div className="font-mono text-sm text-slate-100">{c.title}</div>
                    <div className="overline mt-1">{c.aircraft_type} · {c.departure} → {c.destination} · {c.start_date}</div>
                  </div>
                  {c.pay_rate && <div className="font-mono text-avail text-sm">{c.pay_rate}</div>}
                </div>
              </Link>
            ))}
            {contracts.length === 0 && <div className="overline text-slate-500">NO OPEN CONTRACTS</div>}
          </div>
        </Card>

        {/* AOG feed */}
        <Card testId="dash-aog" className="lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <div className="overline text-orange-hud">AOG LIVE</div>
            <Link to="/aog" data-testid="dash-all-aog" className="btn-hud btn-hud-orange">VIEW ALL</Link>
          </div>
          <div className="space-y-3">
            {aog.map((a) => (
              <div key={a.id} className="border-l-2 pl-3" style={{ borderColor: a.urgency === "critical" ? "#FF3B30" : "#FF9F1C" }}>
                <div className="font-mono text-xs text-slate-100">{a.title}</div>
                <div className="overline mt-1">{a.location} · {a.aircraft_type}</div>
              </div>
            ))}
            {aog.length === 0 && <div className="overline text-slate-500">ALL BIRDS FLYING</div>}
          </div>
        </Card>

        {/* Community activity */}
        <Card testId="dash-forum" className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="overline text-cyan">COMMUNITY ACTIVITY</div>
            <Link to="/forum" data-testid="dash-all-forum" className="btn-hud">VIEW FORUM <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
          <div className="space-y-3">
            {topics.map((t) => (
              <Link key={t.id} to={`/forum/${t.id}`} className="block border border-cyan/10 hover:border-cyan/40 transition-colors p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Badge tone="cyan">{t.category.toUpperCase()}</Badge>
                  <div className="overline">{t.author_name}</div>
                  {t.faa_verified && <ShieldCheck className="h-3 w-3 text-avail" />}
                </div>
                <div className="font-mono text-sm text-slate-100">{t.title}</div>
                <div className="overline mt-1"><MessageSquare className="h-3 w-3 inline mr-1" /> {t.reply_count || 0} replies</div>
              </Link>
            ))}
          </div>
        </Card>

        {/* Quick actions */}
        <Card testId="dash-quick" className="lg:col-span-3">
          <div className="overline text-cyan mb-4">QUICK ACTIONS</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <QuickAction to="/jobs" icon={Plane} label="Post a Job" />
            <QuickAction to="/aog" icon={AlertTriangle} label="Report AOG" tone="orange" />
            <QuickAction to="/marketplace" icon={Store} label="List Aircraft or Part" />
            <QuickAction to="/aerointel" icon={Sparkles} label="Ask AeroIntel" tone="orange" />
          </div>
        </Card>
      </div>
    </div>
  );
}

function Instrument({ icon: Icon, label, value, tone = "cyan", to, testId }) {
  const accent = tone === "orange" ? "#FF7A45" : "#4B9CFF";
  const color = tone === "orange" ? "text-orange-hud" : "text-cyan";

  const body = (
    <div className="relative isolate group">
      {/* Accent glow blob behind the glass */}
      <div
        aria-hidden="true"
        className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] h-[280px] rounded-full opacity-40 pointer-events-none transition-opacity duration-500 group-hover:opacity-70"
        style={{ background: `radial-gradient(circle, ${accent}55 0%, transparent 70%)`, filter: "blur(60px)" }}
      />
      <div
        className="relative rounded-2xl p-5 bg-white/[0.03] backdrop-blur-2xl backdrop-saturate-150 border transition-all duration-500 group-hover:-translate-y-0.5 hud-corners overflow-hidden"
        style={{
          borderColor: `${accent}30`,
          boxShadow: `0 1px 0 rgba(255,255,255,0.05) inset, 0 20px 40px -20px rgba(0,0,0,0.5), 0 0 60px -30px ${accent}55`,
        }}
      >
        {/* Top-edge light-catch highlight */}
        <span
          aria-hidden="true"
          className="absolute top-0 left-5 right-5 h-px pointer-events-none"
          style={{ background: `linear-gradient(90deg, transparent, ${accent}88, transparent)` }}
        />
        <div className="flex items-start justify-between">
          <Icon className={`h-5 w-5 ${color}`} />
          <div className="overline">{label}</div>
        </div>
        <div className={`font-mono text-4xl mt-3 ${color}`}>{value}</div>
      </div>
    </div>
  );

  if (to) {
    return (
      <Link to={to} data-testid={testId || `instrument-${label.toLowerCase().replace(/\s+/g, "-")}`} className="block">
        {body}
      </Link>
    );
  }
  return body;
}

/**
 * PilotCard — glassmorphic identity tile mirroring what visitors see when
 * they land on a member's profile from Directory search: avatar, name,
 * role, home base city, up to 4 type ratings and live availability status.
 * Sits in the Overview column of the cockpit.
 */
function PilotCard({ user, embedded }) {
  const roleLabel = (user.role || "member").replace(/_/g, " ").toUpperCase();
  const homeBase = user.location_icao
    ? `${user.location_icao}${user.location ? ` · ${user.location}` : ""}`
    : (user.location || user?.role_profile?.base_airport || user?.role_profile?.based_at_icao || "");
  const types = Array.isArray(user.aircraft_types) ? user.aircraft_types.filter(Boolean) : [];
  const available = !!user.available_to_work;
  const avatarSrc = user.avatar_url
    ? (user.avatar_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${user.avatar_url}` : user.avatar_url)
    : null;

  const editHref = embedded ? "#profile" : "/profile";
  const onEdit = embedded
    ? (e) => { e.preventDefault(); document.getElementById("profile")?.scrollIntoView({ behavior: "smooth", block: "start" }); }
    : undefined;

  return (
    <div className="relative isolate lg:col-span-1 overflow-hidden" data-testid="dash-profile-card">
      {/* Ambient glow */}
      <div
        aria-hidden="true"
        className="absolute -z-10 top-1/3 left-1/2 -translate-x-1/2 w-[320px] h-[320px] rounded-full opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(75,156,255,0.45) 0%, transparent 70%)", filter: "blur(70px)" }}
      />
      <div
        className="relative h-full rounded-2xl bg-white/[0.03] backdrop-blur-2xl backdrop-saturate-150 border border-cyan/25 overflow-hidden hud-corners"
        style={{
          boxShadow: "0 1px 0 rgba(255,255,255,0.06) inset, 0 20px 50px -20px rgba(0,0,0,0.55), 0 0 80px -40px rgba(75,156,255,0.55)",
        }}
      >
        {/* Top-edge light-catch */}
        <span
          aria-hidden="true"
          className="absolute top-0 left-6 right-6 h-px pointer-events-none"
          style={{ background: "linear-gradient(90deg, transparent, rgba(75,156,255,0.85), transparent)" }}
        />

        {/* Header: avatar + name + availability chip */}
        <div className="p-5 pb-4 flex items-start gap-4">
          <div className="relative flex-shrink-0">
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt=""
                className="h-20 w-20 rounded-full object-cover border-2 border-cyan/50"
                data-testid="pilot-card-avatar"
              />
            ) : (
              <div className="h-20 w-20 rounded-full bg-gradient-to-br from-cyan/40 to-cyan/10 border-2 border-cyan/50 grid place-items-center font-display text-2xl font-bold text-white">
                {(user.first_name || "?").charAt(0)}{(user.last_name || "").charAt(0)}
              </div>
            )}
            {user.faa_verified && (
              <span
                className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-avail grid place-items-center border-2 border-obsidian"
                title="FAA verified"
              >
                <ShieldCheck className="h-3.5 w-3.5 text-obsidian" />
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// PILOT CARD</div>
            <div className="font-display text-xl font-bold text-white leading-tight truncate mt-0.5" data-testid="pilot-card-name">
              {user.first_name} {user.last_name}
            </div>
            <div className="mono text-[10px] tracking-widest uppercase text-white/55 mt-0.5">{roleLabel}</div>
            <div className="mt-2">
              <span
                className={`inline-flex items-center gap-1.5 mono text-[10px] tracking-widest uppercase px-2 py-1 rounded-full border ${
                  available
                    ? "text-avail bg-avail/[0.10] border-avail/40"
                    : "text-white/55 bg-white/[0.04] border-white/15"
                }`}
                data-testid="pilot-card-availability"
              >
                <span className={`h-1.5 w-1.5 rounded-full ${available ? "bg-avail animate-pulse" : "bg-white/40"}`} />
                {available ? "Available" : "Not available"}
              </span>
            </div>
          </div>
        </div>

        {/* Home base + type ratings */}
        <div className="px-5 pb-5 space-y-3">
          <div className="rounded-lg border border-white/10 bg-obsidian/40 px-3 py-2 flex items-center gap-2" data-testid="pilot-card-home-base">
            <MapPin className="h-3.5 w-3.5 text-cyan/80 flex-shrink-0" />
            <div className="min-w-0">
              <div className="mono text-[9px] tracking-widest uppercase text-white/45">Home base</div>
              <div className="font-mono text-sm text-white truncate">{homeBase || "— add on profile —"}</div>
            </div>
          </div>

          <div data-testid="pilot-card-types">
            <div className="mono text-[9px] tracking-widest uppercase text-white/45 mb-1.5">Type ratings</div>
            {types.length === 0 ? (
              <div className="text-[12px] text-white/45">No type ratings on file yet.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {types.slice(0, 6).map((t) => (
                  <span
                    key={t}
                    className="mono text-[10px] tracking-widest uppercase text-cyan border border-cyan/40 bg-cyan/[0.08] px-2 py-1 rounded"
                  >
                    {t}
                  </span>
                ))}
                {types.length > 6 && (
                  <span className="mono text-[10px] tracking-widest uppercase text-white/55 border border-white/20 bg-white/[0.04] px-2 py-1 rounded">
                    +{types.length - 6}
                  </span>
                )}
              </div>
            )}
          </div>

          {user.faa_verified && user.faa_certificate && (
            <div className="mono text-[9px] tracking-widest uppercase text-white/45">
              FAA ID · <span className="text-cyan">{user.faa_certificate}</span>
              {typeof user.total_hours === "number" && user.total_hours > 0 && (
                <> · {user.total_hours.toLocaleString()} hrs</>
              )}
            </div>
          )}

          <a
            href={editHref}
            onClick={onEdit}
            data-testid="dash-manage-profile"
            className="btn-hud btn-hud-solid w-full justify-center mt-1"
          >
            {embedded ? "EDIT PROFILE" : "MANAGE PROFILE"} <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
}


function QuickAction({ to, icon: Icon, label, tone = "cyan" }) {
  return (
    <Link to={to} data-testid={`qa-${label.toLowerCase().replace(/\s+/g, "-")}`} className={`btn-hud ${tone === "orange" ? "btn-hud-orange" : ""} justify-center py-4`}>
      <Icon className="h-4 w-4" /> {label.toUpperCase()}
    </Link>
  );
}

function OperatorFleetPanel({ fleet, user }) {
  const rp = user?.role_profile || {};
  const companyName = rp.company_name || user?.first_name + " " + user?.last_name;
  const cert = rp.part_135_certificate;
  const d085 = rp.d085_file_id;
  const fuelEnrolled = fleet.filter((a) => a.pjp_fuel_active).length;
  const adsbCount = fleet.filter((a) => a.adsb_hex_code).length;
  const [showMap, setShowMap] = useState(adsbCount > 0);
  const [emptyLegFor, setEmptyLegFor] = useState(null);  // aircraft object or null

  return (
    <Card testId="dash-operator-fleet" className="mb-8 border-cyan/25">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="overline text-cyan mb-1 flex items-center gap-2">
            <Building2 className="h-3.5 w-3.5" /> OPERATOR CONSOLE
          </div>
          <div className="font-display text-2xl font-bold tracking-tight">{companyName}</div>
          <div className="mono text-xs text-white/55 mt-1">
            {cert ? <>Part 135 · <span className="text-cyan">{cert}</span></> : <span className="text-orange-hud">Certificate # pending</span>}
            {" · "}
            {d085 ? <span className="text-avail">D085 uploaded</span> : <span className="text-orange-hud">D085 not uploaded</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/operators/onboard?step=fleet" data-testid="op-manage-profile" className="btn-hud">
            <Edit3 className="h-3.5 w-3.5" /> EDIT FLEET
          </Link>
          <Link to="/operators/onboard?step=fleet&new=1" data-testid="op-add-aircraft" className="btn-hud btn-hud-solid">
            <PlusCircle className="h-3.5 w-3.5" /> ADD AIRCRAFT
          </Link>
        </div>
      </div>

      {/* Fleet stats strip */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <MiniStat icon={Plane} label="FLEET" value={fleet.length} />
        <MiniStat icon={Fuel} label="PJP FUEL" value={`${fuelEnrolled}/${fleet.length || 0}`} />
        <MiniStat icon={Radar} label="ADS-B WIRED" value={adsbCount} />
      </div>

      {/* Live map — collapsible. Only wired to real data if at least one tail has a hex code. */}
      {fleet.length > 0 && (
        <div className="mb-5">
          <button
            onClick={() => setShowMap((v) => !v)}
            data-testid="op-toggle-fleet-map"
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-cyan/20 hover:border-cyan/40 bg-cyan/[0.03] hover:bg-cyan/[0.06] transition-colors mb-3"
          >
            <span className="flex items-center gap-2 text-sm mono text-cyan">
              <Radar className="h-3.5 w-3.5" />
              {showMap ? "Hide" : "Show"} live fleet map
              {adsbCount === 0 && (
                <span className="text-[10px] text-orange-hud">· no ADS-B hex codes on file</span>
              )}
            </span>
            {showMap ? <ChevronUp className="h-4 w-4 text-cyan" /> : <ChevronDown className="h-4 w-4 text-cyan" />}
          </button>
          {showMap && <FleetLiveMap />}
        </div>
      )}

      {/* Fleet grid or empty state */}
      {fleet.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-white/10 rounded-lg" data-testid="op-fleet-empty">
          <Plane className="h-8 w-8 text-white/30 mx-auto mb-3" />
          <div className="font-display text-lg font-semibold mb-1">No aircraft in your fleet yet.</div>
          <div className="text-sm text-white/55 mb-4">Add your first tail to start receiving charter quote requests.</div>
          <Link to="/operators/onboard" className="btn-hud btn-hud-solid inline-flex">
            <PlusCircle className="h-3.5 w-3.5" /> ADD FIRST AIRCRAFT
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="op-fleet-grid">
          {fleet.slice(0, 6).map((ac) => (
            <div key={ac.id} className="flex items-center gap-3 p-3 rounded-lg border border-white/10 bg-black/30" data-testid={`op-fleet-card-${ac.tail_number}`}>
              {ac.exterior_photos?.[0] ? (
                <img src={`/api/files/${ac.exterior_photos[0]}`} alt="" className="h-14 w-14 object-cover rounded-md" />
              ) : (
                <div className="h-14 w-14 rounded-md bg-white/[0.03] flex items-center justify-center text-white/40">
                  <Plane className="h-5 w-5" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="mono text-sm font-bold text-cyan truncate">{ac.tail_number}</div>
                <div className="text-[11px] text-white/55 truncate">
                  {ac.type_code}{ac.home_base_icao ? ` · ${ac.home_base_icao}` : ""}
                  {ac.hourly_rate_usd ? ` · $${Number(ac.hourly_rate_usd).toLocaleString()}/hr` : ""}
                </div>
                <div className="flex gap-1 mt-1">
                  {ac.pjp_fuel_active && <span className="mono text-[9px] px-1.5 py-0.5 rounded-full bg-avail/10 text-avail border border-avail/30">FUEL PAID</span>}
                  {ac.adsb_hex_code && <span className="mono text-[9px] px-1.5 py-0.5 rounded-full bg-cyan/10 text-cyan border border-cyan/30">ADS-B</span>}
                  {!ac.hourly_rate_usd && <span className="mono text-[9px] px-1.5 py-0.5 rounded-full bg-orange-hud/10 text-orange-hud border border-orange-hud/30">SET RATE</span>}
                </div>
              </div>
              <Link
                to={`/operators/onboard?step=fleet&edit=${encodeURIComponent(ac.id)}`}
                data-testid={`op-fleet-edit-${ac.tail_number}`}
                className="btn-hud"
                title={`Edit ${ac.tail_number}`}
              >
                <Edit3 className="h-3 w-3" />
              </Link>
              <button
                onClick={() => setEmptyLegFor(ac)}
                data-testid={`op-fleet-empty-leg-${ac.tail_number}`}
                className="btn-hud"
                title={`PTT ${ac.tail_number} to Hot Mic`}
              >
                <Megaphone className="h-3 w-3" />
              </button>
            </div>
          ))}
          {fleet.length > 6 && (
            <Link to="/operators/onboard" className="flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed border-cyan/40 bg-cyan/[0.03] text-cyan text-sm mono hover:bg-cyan/[0.06]">
              +{fleet.length - 6} more · manage all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      )}

      {emptyLegFor && (
        <div className="mt-5">
          <EmptyLegInlinePanel
            aircraft={emptyLegFor}
            companyName={companyName}
            onClose={() => setEmptyLegFor(null)}
          />
        </div>
      )}
    </Card>
  );
}

function EmptyLegInlinePanel({ aircraft, companyName, onClose }) {
  const nav = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    location_icao: aircraft.home_base_icao || "",
    start_date: today,
    end_date: "",
    direction: "any",
    price_hint: aircraft.hourly_rate_usd ? `$${Number(aircraft.hourly_rate_usd).toLocaleString()}/hr` : "",
    extra: "",
  });
  const [busy, setBusy] = useState(false);

  const fmtDate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" }).replace(/\//g, "/") : "";
  const compose = () => {
    const parts = [];
    parts.push(`${companyName} · ${aircraft.tail_number} · ${aircraft.type_code}`);
    const at = form.location_icao ? ` @${form.location_icao.toUpperCase()}` : "";
    const dates = form.start_date && form.end_date
      ? ` available ${fmtDate(form.start_date)}–${fmtDate(form.end_date)}`
      : form.start_date ? ` available ${fmtDate(form.start_date)}` : "";
    parts.push(`Ready for charter${at}${dates}.`);
    if (form.direction && form.direction !== "any") {
      parts.push(`Looking to fill ${form.direction}.`);
    }
    if (form.price_hint) parts.push(`Rate ${form.price_hint}.`);
    if (form.extra) parts.push(form.extra.trim());
    parts.push("DM the operator or reply to book.");
    return parts.join(" ");
  };
  const [text, setText] = useState(compose());
  // Recompose whenever a structured field changes UNLESS the user has hand-edited text.
  const [manuallyEdited, setManuallyEdited] = useState(false);
  useEffect(() => {
    if (!manuallyEdited) setText(compose());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const post = async () => {
    if (!text.trim()) return toast.error("Post text can't be empty");
    setBusy(true);
    try {
      const hashtags = ["emptyleg", aircraft.tail_number, (aircraft.type_code || "").replace(/[^A-Za-z0-9]/g, "")].filter(Boolean);
      await api.post("/social/posts", {
        text,
        hashtags,
        location_icao: (form.location_icao || "").toUpperCase() || null,
      });
      toast.success("PTT to Hot Mic");
      onClose();
      nav("/feed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post failed");
    } finally { setBusy(false); }
  };

  return (
    <Card
      ref={(el) => { if (el && !el.dataset.scrolled) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.dataset.scrolled = "1"; } }}
      className="border-cyan/40 animate-fade-up"
      testId="empty-leg-modal"
    >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-cyan" />
            <div>
              <div className="overline text-cyan">// HOT MIC · PTT JUMPSEAT</div>
              <div className="font-display text-lg font-bold mt-0.5">{aircraft.tail_number} · {aircraft.type_code}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="empty-leg-close"><X className="h-4 w-4" /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="overline text-white/50 mb-1">CURRENTLY AT</div>
            <AirportInput
              testId="empty-leg-icao"
              value={form.location_icao}
              onChange={(v) => setForm({ ...form, location_icao: v })}
              placeholder="KVNY"
            />
          </div>
          <div>
            <div className="overline text-white/50 mb-1">DIRECTION LOOKING</div>
            <select
              data-testid="empty-leg-direction"
              className="input"
              value={form.direction}
              onChange={(e) => setForm({ ...form, direction: e.target.value })}
            >
              <option value="any">Any direction</option>
              <option value="east">East</option>
              <option value="west">West</option>
              <option value="north">North</option>
              <option value="south">South</option>
              <option value="northeast">Northeast</option>
              <option value="northwest">Northwest</option>
              <option value="southeast">Southeast</option>
              <option value="southwest">Southwest</option>
              <option value="international">International</option>
            </select>
          </div>
          <div>
            <div className="overline text-white/50 mb-1">AVAILABLE FROM</div>
            <input
              type="date"
              data-testid="empty-leg-start"
              className="input"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            />
          </div>
          <div>
            <div className="overline text-white/50 mb-1">AVAILABLE THROUGH</div>
            <input
              type="date"
              data-testid="empty-leg-end"
              className="input"
              value={form.end_date}
              min={form.start_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <div className="overline text-white/50 mb-1">RATE (OPTIONAL)</div>
            <input
              data-testid="empty-leg-rate"
              className="input mono"
              value={form.price_hint}
              onChange={(e) => setForm({ ...form, price_hint: e.target.value })}
              placeholder={aircraft.hourly_rate_usd ? `$${Number(aircraft.hourly_rate_usd).toLocaleString()}/hr` : "$7,500/hr"}
            />
          </div>
        </div>

        <div className="mt-4">
          <div className="overline text-white/50 mb-1">POST PREVIEW · EDITABLE</div>
          <textarea
            data-testid="empty-leg-text"
            className="input w-full"
            rows={5}
            value={text}
            onChange={(e) => { setText(e.target.value); setManuallyEdited(true); }}
          />
          <div className="mt-1 text-[10px] text-white/40 mono">
            Auto-composed from the fields above. Edit freely — your changes will be posted verbatim.
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn" data-testid="empty-leg-cancel">Cancel</button>
          <button
            onClick={post}
            disabled={busy}
            data-testid="empty-leg-post"
            className="btn btn-primary"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5" /> PTT to Hot Mic</>}
          </button>
        </div>
    </Card>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/30 p-3 flex items-center gap-3">
      <Icon className="h-4 w-4 text-cyan" />
      <div>
        <div className="overline text-white/50">{label}</div>
        <div className="mono text-xl text-white">{value}</div>
      </div>
    </div>
  );
}

/**
 * EmptyLegPromo — dashboard card promoting the PJP Empty Leg Program.
 * • Charter operators see the "earn 15% rev-share" pitch.
 * • Everyone else sees the "$10K/year rider" pitch.
 * Keeps the placement on both member and operator dashboards without
 * doubling up on marketing surface area.
 */
/**
 * EmptyLegPromo — dashboard JumpSeat card, gate-aware.
 *
 * For operators: unchanged — links to /empty-leg#operator.
 * For every other role: reads /empty-leg/status.gate_reason to render the
 * right CTA:
 *   · has_access (paid Rider, verified Pro crew, admin, or founding member)
 *       → "OPEN JUMPSEAT" → /empty-leg/rider (or /empty-leg for browsing)
 *   · needs_pro (free user)
 *       → "UPGRADE TO PRO" → /membership/addon?tier=pro
 *   · needs_faa_verify (Pro pilot/mech w/o FAA poll)
 *       → "VERIFY YOUR FAA RECORD" → /profile
 *   · needs_fa_verify (Pro FA w/o admin approval)
 *       → "UPLOAD FA DOCS" → /verify/flight-attendant
 *   · role_not_crew (Pro subscriber on non-crew role)
 *       → "GET RIDER — $10K/YR" → /empty-leg#rider
 */
function EmptyLegPromo({ isOperator }) {
  const [gate, setGate] = React.useState(null);
  const [thisWeek, setThisWeek] = React.useState(null);
  const [flownCount, setFlownCount] = React.useState(0);

  React.useEffect(() => {
    if (isOperator) return;
    api.get("/empty-leg/status")
      .then((r) => setGate({ has_access: r.data.has_access, reason: r.data.gate_reason }))
      .catch(() => setGate({ has_access: false, reason: "needs_pro" }));
  }, [isOperator]);

  // When the user has access, prefetch this week's available JumpSeats and
  // their own reservation history so the panel becomes a live snapshot
  // instead of a static upsell.
  React.useEffect(() => {
    if (!gate?.has_access) return;
    api.get("/empty-leg/slots").then((r) => {
      const now = new Date();
      const end = new Date(now.getTime() + 7 * 24 * 3600 * 1000);
      const upcoming = (r.data.slots || []).filter((s) => {
        try {
          const d = new Date(s.departure_date + "T00:00:00");
          return d >= new Date(now.toISOString().slice(0, 10)) && d <= end && s.is_active !== false;
        } catch { return false; }
      });
      setThisWeek(upcoming);
    }).catch(() => setThisWeek([]));
    api.get("/empty-leg/reservations/mine").then((r) => {
      const confirmed = (r.data.reservations || []).filter((x) => (x.reservation?.status || x.status) === "confirmed");
      setFlownCount(confirmed.length);
    }).catch(() => setFlownCount(0));
  }, [gate?.has_access]);

  // Operator variant — original design, unchanged.
  if (isOperator) {
    return (
      <Link
        to="/empty-leg#operator"
        data-testid="dash-empty-leg-promo"
        className="block mb-8 rounded-xl p-4 md:p-5 relative overflow-hidden border transition-colors border-amber-400/40 hover:border-amber-400/70"
        style={{ background: "linear-gradient(155deg, rgba(251,191,36,0.10) 0%, rgba(5,8,16,0.85) 65%)" }}
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-md border border-amber-400/40 bg-amber-400/10">
              <TrendingUp className="h-5 w-5 text-amber-300" />
            </div>
            <div>
              <div className="mono text-[10px] tracking-[0.24em] uppercase text-amber-300">// OPERATOR · JUMPSEAT PROGRAM</div>
              <div className="font-display text-lg md:text-xl font-bold text-white mt-0.5">Monetize your empty ferries — earn 15% rev-share.</div>
              <div className="text-[12px] text-white/60 mt-1 max-w-2xl">Free to join. Riders pay $10,000/yr for first-look access — you keep 100% of seat fees plus a quarterly share of every membership.</div>
            </div>
          </div>
          <div className="mono text-[10px] tracking-widest uppercase px-3 py-2 rounded border flex items-center gap-1.5 flex-shrink-0 border-amber-400/50 text-amber-300">
            ENROLL FLEET <ArrowRight className="h-3.5 w-3.5" />
          </div>
        </div>
      </Link>
    );
  }

  // Loading — render a placeholder that matches the final height so the page doesn't jump.
  if (!gate) {
    return <div className="mb-8 h-[92px] rounded-xl border border-cyan/20 bg-cyan/[0.03] animate-pulse" data-testid="dash-empty-leg-promo-loading" />;
  }

  // Paid / verified users see a live JumpSeat snapshot — this week's ferries
  // + how many they've already flown — instead of the generic upsell card.
  if (gate.has_access) {
    return (
      <div
        data-testid="dash-empty-leg-promo"
        data-gate-reason="has_access"
        className="mb-8 rounded-xl p-4 md:p-5 relative overflow-hidden border border-avail/40 hover:border-avail/70 transition-colors"
        style={{ background: "linear-gradient(155deg, rgba(46,213,115,0.14) 0%, rgba(5,8,16,0.85) 65%)" }}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 rounded-md border border-avail/50 bg-white/[0.03]">
              <Plane className="h-5 w-5 text-avail" />
            </div>
            <div className="min-w-0">
              <div className="mono text-[10px] tracking-[0.24em] uppercase text-avail flex items-center gap-2">
                // JUMPSEAT · ACTIVE
                <span className="inline-flex items-center gap-1 mono text-[9px] tracking-widest uppercase text-white/70 border border-white/15 bg-white/[0.04] px-1.5 py-0.5 rounded-full">
                  {flownCount} flown
                </span>
              </div>
              <div className="font-display text-lg md:text-xl font-bold text-white mt-0.5">
                {thisWeek === null
                  ? "Scanning this week's empty ferries…"
                  : thisWeek.length === 0
                    ? "No JumpSeats this week — check back or view all."
                    : `${thisWeek.length} JumpSeat${thisWeek.length === 1 ? "" : "s"} available in the next 7 days.`}
              </div>
              {thisWeek && thisWeek.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5 max-w-3xl" data-testid="dash-empty-leg-week-list">
                  {thisWeek.slice(0, 4).map((s) => (
                    <span
                      key={s.id}
                      className="mono text-[10.5px] tracking-widest uppercase text-white/85 border border-avail/30 bg-avail/[0.06] px-2 py-1 rounded"
                      title={`${s.aircraft_type || ""} · ${(s.seat_price_usd || 0).toLocaleString ? "$" + (s.seat_price_usd||0).toLocaleString() : ""}`}
                    >
                      {s.departure_date?.slice(5)} · {s.origin_icao} → {s.destination_icao}
                    </span>
                  ))}
                  {thisWeek.length > 4 && (
                    <span className="mono text-[10.5px] tracking-widest uppercase text-white/60 border border-white/15 bg-white/[0.03] px-2 py-1 rounded">
                      +{thisWeek.length - 4} more
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <Link
            to="/rider/dashboard"
            data-testid="dash-empty-leg-cta"
            className="mono text-[10px] tracking-widest uppercase px-3 py-2 rounded border flex items-center gap-1.5 flex-shrink-0 border-avail/50 text-avail hover:bg-avail/[0.10]"
          >
            OPEN JUMPSEAT <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    );
  }

  // Variants keyed off `gate.reason`. All share the same card shell.
  const variants = {
    has_access: {
      to: "/empty-leg/rider",
      overline: "// PJP · JUMPSEAT ACCESS",
      title: "JumpSeat unlocked — browse today's empty legs.",
      body: "Your PJP Pro membership includes free JumpSeat access — reserve seats operators would otherwise ferry empty.",
      cta: "OPEN JUMPSEAT",
      tone: "green",
    },
    needs_pro: {
      to: "/membership/addon?tier=pro",
      overline: "// PJP · JUMPSEAT PROGRAM · PRO PERK",
      title: "JumpSeat is a PJP Pro perk — up to 75% off retail charter.",
      body: "PJP Pro members ($129/yr) get FREE JumpSeat access — the same perk non-crew travelers pay $10,000/yr for. Upgrade to unlock.",
      cta: "UPGRADE TO PRO — $129/YR",
      tone: "cyan",
    },
    needs_faa_verify: {
      to: "/profile",
      overline: "// JUMPSEAT · IDENTITY REQUIRED",
      title: "One more step — verify your FAA record.",
      body: "JumpSeat is a crew-only perk. We just need to poll the FAA Airmen Registry to confirm your certificate. Takes 5 seconds.",
      cta: "SYNC FAA RECORD",
      tone: "orange",
    },
    needs_fa_verify: {
      to: "/verify/flight-attendant",
      overline: "// JUMPSEAT · FA VERIFICATION REQUIRED",
      title: "Upload your crew badge to unlock JumpSeat.",
      body: "Flight Attendants aren't in the FAA registry, so we verify by hand. Upload one doc (pay stub / badge / LOE) — approved within 24-48h.",
      cta: "UPLOAD DOCS",
      tone: "orange",
    },
    role_not_crew: {
      to: "/empty-leg#rider",
      overline: "// JUMPSEAT · RIDER TIER",
      title: "JumpSeat at up to 75% off retail charter.",
      body: "For non-crew travelers, JumpSeat access is available via our $10,000/yr Rider tier — priority alerts, unlimited searches, concierge onboarding.",
      cta: "GET RIDER — $10K/YR",
      tone: "cyan",
    },
  };
  const v = variants[gate.reason] || variants.needs_pro;
  const toneMap = {
    cyan:   { border: "border-cyan/40 hover:border-cyan/70",       icon: "text-cyan",         bg: "linear-gradient(155deg, rgba(75,156,255,0.12) 0%, rgba(5,8,16,0.85) 65%)",   accent: "border-cyan/50 text-cyan" },
    green:  { border: "border-avail/40 hover:border-avail/70",     icon: "text-avail",        bg: "linear-gradient(155deg, rgba(46,213,115,0.14) 0%, rgba(5,8,16,0.85) 65%)",  accent: "border-avail/50 text-avail" },
    orange: { border: "border-orange-hud/40 hover:border-orange-hud/70", icon: "text-orange-hud", bg: "linear-gradient(155deg, rgba(255,138,61,0.14) 0%, rgba(5,8,16,0.85) 65%)", accent: "border-orange-hud/50 text-orange-hud" },
  };
  const t = toneMap[v.tone];

  return (
    <Link
      to={v.to}
      data-testid="dash-empty-leg-promo"
      data-gate-reason={gate.reason}
      className={`block mb-8 rounded-xl p-4 md:p-5 relative overflow-hidden border transition-colors ${t.border}`}
      style={{ background: t.bg }}
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-md border ${t.accent.split(" ")[0]} bg-white/[0.03]`}>
            <Plane className={`h-5 w-5 ${t.icon}`} />
          </div>
          <div>
            <div className={`mono text-[10px] tracking-[0.24em] uppercase ${t.icon}`}>{v.overline}</div>
            <div className="font-display text-lg md:text-xl font-bold text-white mt-0.5">{v.title}</div>
            <div className="text-[12px] text-white/60 mt-1 max-w-2xl">{v.body}</div>
          </div>
        </div>
        <div className={`mono text-[10px] tracking-widest uppercase px-3 py-2 rounded border flex items-center gap-1.5 flex-shrink-0 ${t.accent}`}>
          {v.cta} <ArrowRight className="h-3.5 w-3.5" />
        </div>
      </div>
    </Link>
  );
}

